/**
 * IoTAttributeField Component
 *
 * Attribute field that fetches value from IoT sensors
 * Supports manual override and refresh
 */

import React, { useEffect, useState, useMemo, useRef } from "react"
import { Input, VStack, HStack, Text, Spinner, Badge, IconButton } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import Popover from "@hydrologiq/shared/chakra/Popover"
import type { AttributeConfig, ActivityContext, ValidationResult } from "@hydrologiq/shared/utils/attribute-rules"
import { useIoTAttribute } from "@hydrologiq/shared/utils/attribute-rules/hooks"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { getAsset } from "@hydrologiq/shared/utils/attribute-rules/shared/attributes/asset"
import { FaRotateRight } from "react-icons/fa6"

export interface IoTAttributeFieldProps {
  /** Attribute configuration with iotSource */
  config: AttributeConfig

  /** Current activity context */
  context: ActivityContext

  /** Current field value (may be manual override) */
  value: string

  /** Called when value changes */
  onChange: (value: string) => void
}

/**
 * IoT-enabled attribute field
 *
 * Fetches sensor data automatically, displays loading/error states
 * Allows manual override when editable
 */
export const IoTAttributeField: React.FC<IoTAttributeFieldProps> = ({ config, context, value, onChange }) => {
  const asset = getAsset(context)
  console.log({ asset, context })

  const {
    value: iotValue,
    loading,
    refetch,
  } = useIoTAttribute({ config, context, asset: asset as gqlTypes.AssetBasic | undefined })

  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null)

  // Track if user just clicked refresh button
  const isRefreshingRef = useRef(false)

  // Check if IoT is enabled and monitoring is active
  // Get asset from order (assets are now assigned at order level)
  const isIoTEnabled = config.iotSource && (asset?.monitoring_status || (asset as gqlTypes.Asset)?.assignments)

  // Compute manual override status (derived state - no setState needed, prevents re-renders)
  const isManualOverride = useMemo(() => {
    return iotValue !== null && value !== "" && value !== iotValue
  }, [iotValue, value])

  // Apply default value or IoT value on initial load
  // Priority: IoT value > default value > empty
  useEffect(() => {
    // Only apply on initial load when field is empty
    if (!value) {
      // IoT value takes precedence if available
      if (iotValue !== null) {
        onChange(iotValue)
      } else if (config.defaultValue) {
        // Fallback to default value if no IoT data
        const defaultVal = config.defaultValue(context)
        if (defaultVal) {
          onChange(defaultVal)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iotValue, loading]) // Run when IoT data loads or loading state changes

  // Apply IoT value after refresh (even if field has a value)
  // This separate effect handles the refresh case
  useEffect(() => {
    // Only apply if we just refreshed AND iotValue exists
    if (isRefreshingRef.current && iotValue !== null) {
      onChange(iotValue)
      isRefreshingRef.current = false
    }
  }, [iotValue, onChange])

  // Validate on value change
  // Only re-validate when value or validator changes, not on every context change
  useEffect(() => {
    if (config.validator) {
      const result = config.validator(value, context)
      setValidationResult(result)
    } else {
      setValidationResult(null)
    }
  }, [value, config.validator]) // Removed context from dependencies to prevent re-validation on every render

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleRefresh = async () => {
    // Set refresh flag to indicate we want to apply the new IoT value
    isRefreshingRef.current = true
    // Refetch will update iotValue through the hook, which triggers the effect to apply it
    await refetch()
  }

  // Evaluate editable - can be boolean or function
  const isEditable = typeof config.editable === "function" ? config.editable(context) : config.editable
  const isDisabled = !isEditable
  const inputType = getInputType(config.attribute)

  return (
    <Field
      label={
        config.helpText ? (
          <HStack gap={1}>
            <Text>{config.label || config.attribute}</Text>
            <Popover
              placement="top"
              iconType="info"
              ariaLabel={`${config.label || config.attribute} information`}
              size="xs"
            >
              <Text fontSize="sm" p={2} maxW="300px">
                {config.helpText}
              </Text>
            </Popover>
          </HStack>
        ) : (
          config.label || config.attribute
        )
      }
      required={config.required}
      invalid={validationResult?.valid === false}
      errorText={validationResult?.error}
    >
      <VStack align="stretch" gap={2}>
        <HStack width="100%">
          <Input
            type={inputType}
            value={value}
            onChange={handleChange}
            disabled={isDisabled || loading}
            placeholder={loading ? config.iotSource?.loadingPlaceholder || "Loading..." : "No data"}
            step={inputType === "number" ? "0.1" : undefined}
          />

          {isIoTEnabled && !loading && (
            <IconButton aria-label="Refresh sensor data" size="sm" onClick={handleRefresh} variant={"hydrologiq_icon"}>
              <FaRotateRight />
            </IconButton>
          )}

          {loading && <Spinner size="sm" />}
        </HStack>

        {isManualOverride && (
          <HStack>
            <Badge colorPalette="orange" size="sm">
              Manual Override
            </Badge>
            <Text fontSize="xs" color="fg.muted">
              Sensor value: {iotValue || "n/a"}
            </Text>
          </HStack>
        )}

        {validationResult?.warning && (
          <Text fontSize="xs" color="orange.500">
            ⚠️ {validationResult.warning}
          </Text>
        )}
      </VStack>
    </Field>
  )
}

/**
 * Determine HTML input type based on attribute kind
 */
function getInputType(attribute: gqlTypes.ActivityAttributeKind | string): string {
  switch (attribute) {
    case gqlTypes.ActivityAttributeKind.Pressure:
    case gqlTypes.ActivityAttributeKind.Temperature:
    case gqlTypes.ActivityAttributeKind.Quantity:
    case "fill_level":
      return "number"
    default:
      return "text"
  }
}

// Export memoized version to prevent unnecessary re-renders on GraphQL mutations
export default React.memo(IoTAttributeField)
